import { useState } from "react";
import { useResource } from "@/lib/api";
import { GitHubPanel } from "@/components/GitHubPanel";
import { PageHeader, Panel, StatusPill, Loading, ErrorNote, CopyField, JsonView, fmtTime } from "@/components/kit";

const Discovery = ({ urls }) => {
  const [res, setRes] = useState({});
  const check = async (u) => {
    try { const r = await fetch(u); const j = await r.json(); setRes((s) => ({ ...s, [u]: { ok: r.ok, body: j } })); }
    catch (e) { setRes((s) => ({ ...s, [u]: { ok: false, body: { error: e.message } } })); }
  };
  return urls.map((u, i) => (
    <div key={u} className="mb-3">
      <div className="flex gap-2"><div className="min-w-0 flex-1"><CopyField value={u} testId={`discovery-url-${i}`} /></div>
        <button data-testid={`discovery-check-${i}`} onClick={() => check(u)} className="rounded-sm border border-[var(--cc-navy)] px-3 text-xs hover:bg-[var(--cc-navy)] hover:text-white">Verify</button></div>
      {res[u] && <div className="mt-2"><StatusPill status={res[u].ok ? "passed" : "failed"} /><JsonView value={res[u].body} max="max-h-48" testId={`discovery-result-${i}`} /></div>}
    </div>
  ));
};

const STEPS = [
  "In ChatGPT (Plus/Pro/Business/Enterprise), open Settings → Apps & Connectors → Advanced settings and enable Developer mode.",
  "Choose Create (Add custom connector). Name: Cruise Command Developer Gateway. MCP Server URL: the MCP URL above.",
  "Authentication: OAuth. Leave client ID/secret empty — ChatGPT registers itself via dynamic client registration.",
  "ChatGPT opens the gateway consent page. Sign in with Google as seanwheatley74@gmail.com and approve L1 read-only access.",
  "In a new chat, enable the connector and ask: “Call gateway_status”. The gateway audits the call; status C turns Verified.",
];

export default function Connectors() {
  const { data, error, loading, reload } = useResource("/connectors");
  if (loading) return <Loading />;
  if (error) return <ErrorNote error={error} />;
  const gh = data.github;
  return (
    <div>
      <PageHeader eyebrow="Connectivity · Evidence-based" title="Connector Status">
        <a href="#github" data-testid="jump-to-github" className="rounded-sm bg-[var(--cc-navy)] px-4 py-2 text-xs font-medium text-white hover:bg-[var(--cc-signal)] hover:text-[var(--cc-navy)]">Upload GitHub key ↓</a>
      </PageHeader>
      <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Panel title="A · Gateway" aside={<StatusPill status="operational" testId="status-gateway" />}><CopyField value={data.gateway.url} testId="gateway-url" /></Panel>
        <Panel title="B · OpenAI API Console" aside={<StatusPill status={data.openai_console.status} testId="status-console" />}>
          <p className="text-sm">Model <code className="text-xs">{data.openai_console.model}</code> via Emergent Universal Key.</p>
          <p className="mt-1 text-xs text-[var(--cc-muted)]">Last success: {fmtTime(data.openai_console.last_success)}</p></Panel>
        <Panel title="C · Direct ChatGPT" aside={<StatusPill status={data.chatgpt.status} testId="status-chatgpt" />}>
          <p className="text-xs text-[var(--cc-muted)]">Clients registered: {data.chatgpt.chatgpt_clients_registered} · tokens issued: {data.chatgpt.tokens_issued}</p>
          {data.chatgpt.evidence ? <p className="mt-2 text-xs">Evidence: audit #{data.chatgpt.evidence.seq} {data.chatgpt.evidence.action} at {fmtTime(data.chatgpt.evidence.timestamp)}</p>
            : <p className="mt-2 text-xs">Not verified. An audited, authenticated ChatGPT tool call is required — the existence of the endpoint is not evidence.</p>}</Panel>
      </div>
      <div className="mb-6" id="github"><GitHubPanel gh={gh} onChange={reload} /></div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Panel title="MCP endpoint" testId="mcp-panel">
          <CopyField value={data.mcp.url} testId="mcp-url" />
          <p className="mt-3 text-xs text-[var(--cc-muted)]">{data.mcp.transport} · {data.mcp.auth} · scope gateway.read</p>
          <p className="mt-2 text-xs">Last authenticated MCP call: {data.mcp.last_authenticated_tool_call ? `#${data.mcp.last_authenticated_tool_call.seq} ${data.mcp.last_authenticated_tool_call.action} by ${data.mcp.last_authenticated_tool_call.actor}` : "none yet"}</p>
          <h3 className="mb-2 mt-6 text-xs font-bold uppercase tracking-wider">ChatGPT connection steps</h3>
          <ol className="space-y-2 text-sm" data-testid="chatgpt-steps">{STEPS.map((s, i) => <li key={i} className="flex gap-3"><span className="font-display font-extrabold text-[var(--cc-signal)]">{i + 1}</span><span>{s}</span></li>)}</ol>
        </Panel>
        <Panel title="OAuth discovery" testId="oauth-panel">
          <Discovery urls={[data.oauth.protected_resource_metadata, ...data.oauth.authorization_server_metadata]} />
          <p className="break-all text-xs text-[var(--cc-muted)]">Issuer {data.oauth.issuer} · PKCE S256 required · public clients via dynamic registration</p>
        </Panel>
        <Panel title={`MCP tools (${data.tools.length})`} testId="tools-panel">
          <div className="max-h-[420px] space-y-2 overflow-auto">{data.tools.map((t) => (
            <details key={t.name} className="rounded-sm border border-[var(--cc-line)] p-3" data-testid={`tool-${t.name}`}>
              <summary className="cursor-pointer font-mono text-xs font-medium">{t.name} <span className="text-[var(--cc-muted)]">· {t.required_level} · read-only</span></summary>
              <p className="mt-2 text-xs">{t.description}</p><JsonView value={t.inputSchema} max="max-h-40" />
            </details>))}</div>
        </Panel>
      </div>
    </div>
  );
}
